import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { X, CalendarClock, Loader2, Check, Ban } from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TABS = [
  { value: 'aguardando_aprovacao', label: 'Aguardando Aprovação' },
  { value: 'aprovada', label: 'Aprovadas' },
  { value: 'rejeitada', label: 'Rejeitadas' },
  { value: '', label: 'Todas' },
];

const STATUS_BADGE = {
  rascunho: 'bg-gray-700 text-gray-200',
  aguardando_aprovacao: 'bg-yellow-600 text-white',
  aprovada: 'bg-green-600 text-white',
  rejeitada: 'bg-red-600 text-white',
};

const fmtBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (s) => { try { return s ? new Date(s + 'T00:00:00').toLocaleDateString('pt-BR') : '—'; } catch { return s; } };

// Painel de Aprovação de Diárias (Master/Gestor). Lista O.S. com diária gerada.
export default function DiariaPanelModal({ open, onClose, canApprove, onChanged }) {
  const [statusFilter, setStatusFilter] = useState('aguardando_aprovacao');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/diarias${statusFilter ? `?status=${statusFilter}` : ''}`;
      const r = await fetch(url, { headers: authHeaders(), cache: 'no-store' });
      if (r.ok) setItems(await r.json());
      else setItems([]);
    } catch {
      toast.error('Erro ao carregar diárias');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const aprovar = async (wo) => {
    setBusyId(wo.wo_id);
    try {
      const r = await fetch(`${API_URL}/api/work-orders/${wo.wo_id}/diaria/aprovar`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Diária aprovada — ${fmtBRL(data.valor)}`);
        await load();
        onChanged && onChanged();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Erro ao aprovar diária');
      }
    } finally {
      setBusyId(null);
    }
  };

  const rejeitar = async (wo) => {
    const motivo = window.prompt('Motivo da rejeição da diária:');
    if (motivo === null) return;
    if (!motivo.trim()) { toast.error('Motivo é obrigatório'); return; }
    setBusyId(wo.wo_id);
    try {
      const r = await fetch(`${API_URL}/api/work-orders/${wo.wo_id}/diaria/rejeitar`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ motivo: motivo.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success('Diária rejeitada');
        await load();
        onChanged && onChanged();
      } else {
        toast.error(typeof data.detail === 'string' ? data.detail : 'Erro ao rejeitar diária');
      }
    } finally {
      setBusyId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="diaria-panel-modal">
      <div className="bg-[#141414] border border-gray-800 rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-[#141414] z-10">
          <div className="flex items-center gap-2">
            <CalendarClock size={18} className="text-emerald-400" />
            <h3 className="text-base font-semibold text-white">Painel de Diárias</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="diaria-panel-close"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="diaria-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.value || 'all'}
                onClick={() => setStatusFilter(t.value)}
                data-testid={`diaria-tab-${t.value || 'all'}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === t.value ? 'bg-emerald-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-500" /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center" data-testid="diaria-empty">Nenhuma diária encontrada.</p>
          ) : (
            <div className="space-y-2" data-testid="diaria-list">
              {items.map((wo) => (
                <div key={wo.wo_id} className="p-3 rounded-lg bg-[#0A0A0A] border border-gray-800" data-testid={`diaria-item-${wo.wo_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">{wo.colaborador_nome || 'Colaborador —'}</span>
                        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_BADGE[wo.status] || STATUS_BADGE.rascunho}`}>
                          {STATUS_TABS.find((s) => s.value === wo.status)?.label || wo.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400">
                        🔧 O.S. #{wo.order_number} • {wo.machine_nome || 'Máquina —'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {wo.tipo_servico || 'Serviço —'} • {fmtDate(wo.data_inicio_servico)} → {fmtDate(wo.data_fim_servico)} • <strong className="text-emerald-300">{wo.horas_servico_calculadas || 0}h</strong>
                      </p>
                      {wo.valor != null && wo.status === 'aprovada' && (
                        <p className="text-xs text-green-400" data-testid={`diaria-valor-${wo.wo_id}`}>Valor: {fmtBRL(wo.valor)} (R$/h {fmtBRL(wo.valor_hora_aplicado)})</p>
                      )}
                      {wo.status === 'rejeitada' && wo.motivo_rejeicao && (
                        <p className="text-xs text-red-400">Motivo: {wo.motivo_rejeicao}</p>
                      )}
                    </div>
                    {canApprove && wo.status === 'aguardando_aprovacao' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => aprovar(wo)}
                          disabled={busyId === wo.wo_id}
                          data-testid={`diaria-aprovar-${wo.wo_id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg text-xs font-medium disabled:opacity-40"
                        >
                          {busyId === wo.wo_id ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Aprovar
                        </button>
                        <button
                          onClick={() => rejeitar(wo)}
                          disabled={busyId === wo.wo_id}
                          data-testid={`diaria-rejeitar-${wo.wo_id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-xs font-medium disabled:opacity-40"
                        >
                          <Ban size={14} /> Rejeitar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
